import { useMutation, useQueryClient } from '@tanstack/solid-query'
import { isHTTPError } from 'ky'
import { api, getCurrentUserId } from '~/services/api'
import { progressesQueryKey } from './progresses'
import { unlockedTreesQueryKey } from './unlocked-trees'

export type PurchaseError =
  | { kind: 'insufficient-currency' }
  | { kind: 'daily-limit' }

export class PurchaseFailure extends Error {
  constructor(public readonly cause: PurchaseError) {
    super(cause.kind)
    this.name = 'PurchaseFailure'
  }
}

export type PurchaseVariables = {
  productId: number
  quantity?: number
}

type PurchaseResponse = {
  results: Array<{ purchase?: { id: number } }>
}

export const usePurchase = () => {
  const queryClient = useQueryClient()
  return useMutation(() => ({
    mutationFn: async (vars: PurchaseVariables) => {
      const userId = await getCurrentUserId()
      try {
        return await api.service
          .post(`store/users/${userId}/purchase`, {
            json: { product_id: vars.productId, quantity: vars.quantity ?? 1 },
          })
          .json<PurchaseResponse>()
      } catch (error) {
        if (isHTTPError(error)) {
          if (error.response.status === 422) {
            throw new PurchaseFailure({ kind: 'insufficient-currency' })
          }
          if (error.response.status === 409) {
            throw new PurchaseFailure({ kind: 'daily-limit' })
          }
          throw error
        }
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
      queryClient.invalidateQueries({ queryKey: progressesQueryKey })
      queryClient.invalidateQueries({ queryKey: unlockedTreesQueryKey })
    },
  }))
}
